import {
  ArticleList,
  ArticleQuote,
  ArticleTable,
  renderInline,
  type ArticleListBlock,
  type ArticleTableBlock,
} from "@/lib/article-blocks";
import { renderWithLinks, type InlineLink } from "@/lib/blog-text";

/** One block of a long-form article body — the shape shared by English and Bangla posts. */
export type ArticleBodyBlock = {
  h?: string;
  h3?: string;
  p?: string;
  /** Quick/short answer box (speakable). */
  a?: string;
  quote?: string;
  list?: ArticleListBlock;
  table?: ArticleTableBlock;
  /** Text uses inline Markdown instead of the legacy `links` array. */
  md?: boolean;
  links?: InlineLink[];
};

type ArticleVariant = {
  wrapper: string;
  h2: string;
  h3: string;
  answerBox: string;
  answerLabelClass: string;
  answerLabel: string;
  paragraph?: string;
  /** Legacy inline links open in a new tab (outbound citations). */
  externalLinks: boolean;
  /** Answer boxes always render inline Markdown (ignoring legacy links). */
  answerAlwaysMarkdown: boolean;
};

/** Per-language typography and behaviour for article bodies. */
const VARIANTS: Record<"en" | "bn", ArticleVariant> = {
  en: {
    wrapper: "mt-12 space-y-7 text-lg leading-relaxed text-muted-foreground",
    h2: "font-display text-3xl text-foreground mt-12 mb-2",
    h3: "font-display text-2xl text-foreground mt-10 mb-1",
    answerBox: "rounded-2xl bg-card border border-border p-6 -mx-2",
    answerLabelClass: "text-xs uppercase tracking-[0.18em] text-primary mb-2",
    answerLabel: "Quick answer",
    externalLinks: true,
    answerAlwaysMarkdown: false,
  },
  bn: {
    wrapper: "prose-bn space-y-5 text-base leading-[1.9]",
    h2: "font-display text-2xl mt-10 mb-2",
    h3: "font-display text-xl mt-8 mb-1",
    answerBox: "rounded-2xl border border-border bg-card p-6",
    answerLabelClass: "mb-2 text-xs uppercase tracking-[0.18em] text-primary",
    answerLabel: "সংক্ষিপ্ত উত্তর",
    paragraph: "text-foreground/90",
    externalLinks: false,
    answerAlwaysMarkdown: true,
  },
};

/** Renders an article body (headings, paragraphs, answer boxes, tables, lists, quotes). */
export function ArticleBody({ blocks, lang }: { blocks: ArticleBodyBlock[]; lang: "en" | "bn" }) {
  const v = VARIANTS[lang];
  const linked = (text: string, b: ArticleBodyBlock) =>
    b.md ? renderInline(text) : renderWithLinks(text, b.links, { external: v.externalLinks });

  return (
    <div className={v.wrapper}>
      {blocks.map((b, i) => {
        if (b.h)
          return (
            <h2 key={i} className={v.h2}>
              {b.h}
            </h2>
          );
        if (b.h3)
          return (
            <h3 key={i} className={v.h3}>
              {b.h3}
            </h3>
          );
        if (b.table) return <ArticleTable key={i} {...b.table} />;
        if (b.list) return <ArticleList key={i} {...b.list} />;
        if (b.quote) return <ArticleQuote key={i}>{renderInline(b.quote)}</ArticleQuote>;
        if (b.a)
          return (
            <div key={i} className={v.answerBox}>
              <div className={v.answerLabelClass}>{v.answerLabel}</div>
              <p className="text-foreground aeo-speakable">
                {v.answerAlwaysMarkdown ? renderInline(b.a) : linked(b.a, b)}
              </p>
            </div>
          );
        return (
          <p key={i} className={v.paragraph}>
            {linked(b.p ?? "", b)}
          </p>
        );
      })}
    </div>
  );
}
